import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Camera, Image as ImageIcon, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const CATS = [
  ["", "— Sem categoria —"], ["pintura", "Pintura"], ["drywall", "Drywall / Gesso"], ["eletrica", "Elétrica"],
  ["hidraulica", "Hidráulica"], ["alvenaria", "Alvenaria"], ["pedreiro", "Pedreiro"], ["limpeza", "Limpeza / Jardinagem"],
  ["mobiliario", "Mobiliário"], ["cobertura", "Cobertura / Telhado"], ["portas", "Portas e Janelas"], ["piso", "Piso"],
  ["marcenaria", "Marcenaria"], ["outros", "Outros"],
];

// Foto geral do serviço → Galeria de trabalhos (Mídia do site) marcada com a escola.
export default function ServicePhotoForm({ schoolId, schoolName }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);

  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const reset = () => { setFile(null); setCaption(""); setCategory(""); setOpen(false); };

  const save = async () => {
    if (!file || !caption.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption.trim());
      fd.append("section", "trabalhos");
      if (category) fd.append("category", category);
      fd.append("school_id", schoolId);
      await api.post("/landing/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto publicada no mural de serviços");
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar foto");
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="w-full flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 border-dashed border-sky-300 text-sky-700 text-[12px] font-bold hover:bg-sky-50" data-testid="quick-service-photo-open">
        <ImagePlus className="w-4 h-4" /> Foto geral do serviço (mural)
      </button>
    );
  }
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 space-y-2" data-testid="quick-service-photo-form">
      <div className="flex items-center justify-between">
        <p className="text-[12px] font-bold text-sky-900">Foto geral — {schoolName}</p>
        <button type="button" onClick={reset} className="text-slate-400 hover:text-slate-600" aria-label="Fechar"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-[10.5px] text-slate-500">Vai para o mural de serviços da escola e para a Galeria de trabalhos do site. Não aparece nos itens.</p>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="quick-service-photo-camera" />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="quick-service-photo-gallery" />
      {preview ? (
        <div className="relative w-32 aspect-square rounded-lg overflow-hidden border border-slate-200">
          <img src={preview} alt="" className="w-full h-full object-cover" />
          <button type="button" onClick={() => setFile(null)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center" aria-label="Remover"><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => camRef.current?.click()} className="bg-white"><Camera className="w-4 h-4 mr-1.5" />Tirar foto</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => galRef.current?.click()} className="bg-white"><ImageIcon className="w-4 h-4 mr-1.5" />Galeria</Button>
        </div>
      )}
      <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200} rows={2} placeholder="Descrição do serviço (obrigatória)" className="bg-white text-sm" data-testid="quick-service-photo-caption" />
      <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="quick-service-photo-category">
        {CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Button type="button" onClick={save} disabled={!file || !caption.trim() || saving} className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="quick-service-photo-save">
        {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1.5" />}Publicar no mural
      </Button>
    </div>
  );
}
